/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import { useCallback, useMemo, useRef } from 'react';

import { cssMap, jsx } from '@compiled/react';
import type { IntlShape } from 'react-intl';
import { IntlProvider } from 'react-intl';

import { aiSuggestionsMessages } from '@atlaskit/editor-common/ai-messages';
import { useSharedPluginStateWithSelector } from '@atlaskit/editor-common/hooks';
import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types';
import { token } from '@atlaskit/tokens';

import type { AiSuggestionsPlugin } from '../../aiSuggestionsPluginType';
import { aiSuggestionsPluginKey } from '../../pm-plugins/main';
import { getUniqueAvatarProfiles } from '../utils/getUniqueAvatarProfiles';
import { SuggestionAvatarIndicator } from './SuggestionAvatarIndicator';

const styles = cssMap({
	container: {
		position: 'absolute',
		// The transform creates a stacking context; keep it above the table hover mask.
		zIndex: 3,
		transform: 'translateX(100%)',
		pointerEvents: 'all',
	},
});

export function SuggestionIconButton({
	leftAdjustment,
	localId,
	api,
	intl,
	topAdjustment,
}: {
	api?: ExtractInjectionAPI<AiSuggestionsPlugin>;
	intl?: IntlShape;
	leftAdjustment?: string;
	/**
	 * Base localId of the block this button sits on. The only identity this
	 * component needs: the suggestions at that block — and whether one of them is
	 * selected — are read from plugin state below rather than passed in, so the
	 * widget decoration hosting this button can keep a stable key and never
	 * re-create the `--button-<localId>` anchor. See `calculateSuggestionsDecorations`.
	 */
	localId: string;
	// Used to visually adjust the position so it lines up with block controls
	topAdjustment?: number;
}): JSX.Element {
	// Derived primitives (rather than the state maps) so the hook's shallow
	// equality check can skip re-renders that don't affect this button.
	const { firstSuggestionId, isSelected, profiles, shortDescription, suggestionsCount } =
		useSharedPluginStateWithSelector(api, ['aiSuggestions'], (state) => {
			const { suggestionIdsByBaseLocalId, suggestionById, selectedSuggestion } =
				state?.aiSuggestionsState ?? {};
			const suggestionIds = suggestionIdsByBaseLocalId?.get(localId);
			const firstSuggestionId: string | undefined = suggestionIds?.values().next().value;

			return {
				firstSuggestionId,
				isSelected: Boolean(selectedSuggestion && suggestionIds?.has(selectedSuggestion)),
				profiles: getUniqueAvatarProfiles(suggestionIds, suggestionById),
				shortDescription: firstSuggestionId
					? suggestionById?.get(firstSuggestionId)?.shortDescription
					: undefined,
				suggestionsCount: suggestionIds?.size ?? 0,
			};
		});

	const isSelectingSuggestionRef = useRef(false);

	const onSelectSuggestion = useCallback(() => {
		if (isSelectingSuggestionRef.current) {
			return;
		}

		isSelectingSuggestionRef.current = true;

		const selectSuggestion = async (): Promise<void> => {
			try {
				const canCloseComment =
					(await api?.annotation?.actions.requestCloseInlineComment?.()) ?? true;

				if (!canCloseComment || !firstSuggestionId) {
					return;
				}

				api?.aiSuggestions?.actions.selectSuggestion(firstSuggestionId, {
					interactionPoint: 'card',
				});
			} finally {
				isSelectingSuggestionRef.current = false;
			}
		};

		void selectSuggestion();
	}, [api, firstSuggestionId]);

	const setHoveredSuggestion = (id: string | undefined) => {
		api?.core?.actions.execute(({ tr }) => {
			tr.setMeta(aiSuggestionsPluginKey, { action: 'HOVER_SUGGESTION', id });
			return tr;
		});
	};

	const label = useMemo(
		() =>
			suggestionsCount > 1
				? intl?.formatMessage(aiSuggestionsMessages.iconButtonSuggestionsCountLabel, {
						count: suggestionsCount,
					}) ||
					(shortDescription ?? '')
				: (shortDescription ?? ''),
		[intl, shortDescription, suggestionsCount],
	);

	return (
		<IntlProvider
			defaultLocale={intl?.defaultLocale || 'en-US'}
			locale={intl?.locale || 'en-US'}
			messages={intl?.messages}
			formats={intl?.formats}
		>
			<span
				css={styles.container}
				style={{
					anchorName: `--button-${localId}`,
					top: topAdjustment ?? 0,
					right: `calc(${token('space.negative.300')} + ${token('space.negative.300')} - ${
						leftAdjustment ?? '0px'
					})`,
				}}
			>
				{
					// We keep the anchorName span rendered so that the suggestion card can reference it
					!isSelected && firstSuggestionId && (
						<span
							onMouseEnter={() => setHoveredSuggestion(firstSuggestionId)}
							onMouseLeave={() => setHoveredSuggestion(undefined)}
							onFocus={() => setHoveredSuggestion(firstSuggestionId)}
							onBlur={() => setHoveredSuggestion(undefined)}
						>
							<SuggestionAvatarIndicator
								profiles={profiles}
								label={label}
								onClick={onSelectSuggestion}
							/>
						</span>
					)
				}
			</span>
		</IntlProvider>
	);
}
